import { apiClient, getApiUrl } from '../lib/apiClient';
import type {
  Camera,
  CreateCameraRequest,
  UpdateCameraRequest,
  ListQueryParams,
  Pagination,
  ApiResponse
} from '../types/api';
import {
  extractListData,
  extractSingleData,
  extractPagination,
  type RailsWrappedListResponse,
  type RailsSingleWrappedResponse
} from '../utils/railsResponseHandler';

// Camera CRUD operations
export const getCameras = async (params?: ListQueryParams): Promise<{ cameras: Camera[]; pagination: Pagination }> => {
  const response = await apiClient.get<RailsWrappedListResponse<Camera>>(getApiUrl('/cameras'), { params });

  const cameras = extractListData<Camera>(response.data);
  const pagination = extractPagination(response.data) || {
    current_page: 1,
    total_pages: 1,
    total_count: cameras.length,
    per_page: 20,
    has_previous: false,
    has_next: false
  };

  return { cameras, pagination };
};

export const getCamera = async (id: string, view?: string): Promise<Camera> => {
  const params = view ? { view } : {};
  const response = await apiClient.get<RailsSingleWrappedResponse<Camera>>(getApiUrl(`/cameras/${id}`), { params });
  return extractSingleData<Camera>(response.data);
};

export const createCamera = async (data: CreateCameraRequest): Promise<Camera> => {
  const response = await apiClient.post<RailsSingleWrappedResponse<Camera>>(getApiUrl('/cameras'), data);
  return extractSingleData<Camera>(response.data);
};

export const updateCamera = async (id: string, data: UpdateCameraRequest): Promise<Camera> => {
  const response = await apiClient.put<RailsSingleWrappedResponse<Camera>>(getApiUrl(`/cameras/${id}`), data);
  return extractSingleData<Camera>(response.data);
};

export const deleteCamera = async (id: string): Promise<void> => {
  await apiClient.delete(getApiUrl(`/cameras/${id}`));
};

// Camera specific operations
export const testCameraConnection = async (id: string): Promise<ApiResponse<any>> => {
  const response = await apiClient.post(getApiUrl(`/cameras/${id}/test_connection`));
  return response.data;
};

export const toggleCameraStatus = async (id: string): Promise<Camera> => {
  const response = await apiClient.patch<RailsSingleWrappedResponse<Camera>>(getApiUrl(`/cameras/${id}/toggle_status`));
  return extractSingleData<Camera>(response.data);
};

// Camera status monitoring
export const getCameraHealthStatus = async (id: string): Promise<any> => {
  const response = await apiClient.get<RailsSingleWrappedResponse<any>>(getApiUrl(`/cameras/${id}/health`));
  return extractSingleData(response.data);
};

export const getCamerasOverview = async (): Promise<{
  total: number;
  online: number;
  offline: number;
  recording: number;
  health_summary: Record<string, number>;
}> => {
  try {
    const { cameras } = await getCameras({ per_page: 100 });

    const total = cameras.length;
    let online = 0;
    let offline = 0;
    let recording = 0;
    const health_summary: Record<string, number> = {
      healthy: 0,
      warning: 0,
      error: 0,
      offline: 0,
      unknown: 0,
    };

    cameras.forEach(camera => {
      // Count online/offline based on status
      if (camera.status === 'active' && camera.health_status === 'healthy') {
        online++;
      } else {
        offline++;
      }

      // Count recording cameras
      if ((camera as any).recording_enabled) {
        recording++;
      }

      // Count health status
      const healthStatus = camera.health_status || 'unknown';
      health_summary[healthStatus] = (health_summary[healthStatus] || 0) + 1;
    });

    return {
      total,
      online,
      offline,
      recording,
      health_summary,
    };
  } catch (error) {
    console.error('Error getting cameras overview:', error);
    throw error;
  }
};

// Bulk operations
export const bulkToggleCameraStatus = async (ids: string[], _status: boolean): Promise<{ success: string[]; failed: string[] }> => {
  try {
    const results = await Promise.allSettled(
      ids.map(id => toggleCameraStatus(id))
    );

    const success: string[] = [];
    const failed: string[] = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        success.push(ids[index]);
      } else {
        failed.push(ids[index]);
      }
    });

    return { success, failed };
  } catch (error) {
    console.error('Bulk toggle camera status failed:', error);
    throw error;
  }
};

export const bulkTestCameraConnection = async (ids: string[]): Promise<{ success: string[]; failed: string[] }> => {
  try {
    const results = await Promise.allSettled(
      ids.map(id => testCameraConnection(id))
    );

    const success: string[] = [];
    const failed: string[] = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        success.push(ids[index]);
      } else {
        failed.push(ids[index]);
      }
    });

    return { success, failed };
  } catch (error) {
    console.error('Bulk test camera connection failed:', error);
    throw error;
  }
};

export const bulkDeleteCameras = async (ids: string[]): Promise<{ success: string[]; failed: string[] }> => {
  try {
    const results = await Promise.allSettled(
      ids.map(id => deleteCamera(id))
    );

    const success: string[] = [];
    const failed: string[] = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        success.push(ids[index]);
      } else {
        failed.push(ids[index]);
      }
    });

    return { success, failed };
  } catch (error) {
    console.error('Bulk delete cameras failed:', error);
    throw error;
  }
};
